import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { StatusText } from '../../../../components/table/StatusText';
import { useToast } from '../../../../components/toastContext';
import { IssuanceModal } from '../components/IssuanceModal';
import { TextCell } from '../../../../components/table/TextCell';
import type { RequisitionSlipListItem } from '../../../self-service/requisition-slips/requisitionSlipTypes';
import { issuanceRequestsApi } from '../issuanceRequestApi';
import { requestsPageTabs, useIssuanceStripCounts } from '../issuancePageTabs';

/**
 * What offices are asking Logistics for — the Issuances module's Requests tab.
 *
 * **Every office's slip that has been SENT, and nothing else**: Submitted and Approved. A Draft
 * is the requester's own unfinished work; a refused or withdrawn slip asks for nothing. The
 * server fixes that by the route — there is no scope on the wire.
 *
 * **READ-ONLY over the slips.** No row action and no delete — a slip is corrected or withdrawn by
 * its requester. **Create is the register's own** (product call, 03 Nov 2026), so the toolbar
 * reads the same on every tab of the strip: it opens the ordinary Add Item Issuance form, linked
 * to no request. Issuing AGAINST one is that request's Issued Assets tab.
 */
export function IssuanceRequestsIndexPage() {
  const navigate = useNavigate();
  const { requests, stages } = useIssuanceStripCounts(true);
  const toast = useToast();
  const [creating, setCreating] = useState(false);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      issuanceRequestsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
        },
        signal,
      ),
    [],
  );

  return (
    <>
      <IndexPage<RequisitionSlipListItem>
        module="issuance-requests"
        moduleNoun="requests"
        breadcrumb={['Logistics', 'Inventory', 'Issuances']}
        /*
          THE SAME STRIP AS ISSUANCES — Requests active, then the register's stage tabs, which go
          back to Issuances on that stage. This list's own Stage axis stays in the rail: a second
          strip of facet tabs here would be two rows of tabs over one panel.
        */
        pageTabs={requestsPageTabs(navigate, requests, stages)}
        folderNavFacet={null}
        columns={COLUMNS}
        facets={[
          { key: 'stage', label: 'Stage' },
          { key: 'status', label: 'Status' },
        ]}
        // BOTH entity types — a module history naming only the parent is blind to its lines.
        auditEntityTypes={['RequisitionSlip', 'RequisitionSlipItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        searchPlaceholder="Search by slip number, office or purpose"
        emptyMessage="No office has sent a requisition slip yet. Submitted and approved slips appear here."
        onRowOpen={(row) => navigate(`/inventory/issuances/requests/${row.id}`)}
        openPermission="issuances.read-requests"
        otherInfo={(row) => row}
        onCreate={() => setCreating(true)}
        createPermission="issuances.create"
      />

      {creating ? (
        <IssuanceModal
          open
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            toast.success('Stock issued.');
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<RequisitionSlipListItem>[] = [
  {
    /*
      THE SLIP NUMBER, with its PURPOSE — the requester's register's own anchor, so the two
      surfaces over one record name it the same way.
    */
    key: 'slipNumber',
    header: 'Slip number',
    width: 320,
    sortable: true,
    render: (row) => <TextCell code={row.slipNumber} value={row.purpose} clamp />,
  },
  {
    /*
      WHO IS ASKING — the column the requester's own register does not need, because every row
      there is one office. Across the organization it is the first thing a storekeeper reads.
    */
    key: 'requestingOrgUnitName',
    header: 'Requesting office',
    width: 220,
    flex: true,
    summary: true,
    render: (row) => <TextCell value={row.requestingOrgUnitName} />,
  },
  {
    key: 'lineCount',
    header: 'Lines',
    width: 90,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={String(row.lineCount)} mono numeric />,
  },
  {
    key: 'totalQuantity',
    header: 'Requested',
    width: 120,
    sortable: true,
    // A COUNT of things asked for across units — read beside Lines, never acted on alone.
    render: (row) => <TextCell value={String(row.totalRequestedQuantity)} mono numeric />,
  },
  {
    key: 'stage',
    header: 'Stage',
    width: 140,
    sortable: true,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.stageLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
